import React, { useState } from 'react';

/**
 * CookieConsentBanner — GDPR/PECR-compliant consent UI for the public
 * welcome page. Rendered in-flow (bottom-fixed) rather than as a
 * modal so it never blocks navigation.
 *
 * Compliance notes:
 *  • "Accept" and "Reject" buttons are visually equal (same size,
 *    same emphasis) — PECR/GDPR guidance requires reject-tracking be
 *    as easy as accept-tracking.
 *  • Analytics scripts must NOT be injected before consent is given.
 *    That gating happens in WelcomePage.js, which reads the hook.
 *  • Cookie categories are surfaced via a "Preferences" secondary
 *    action so visitors can granularly enable analytics without
 *    accepting the full future set.
 *  • The link to the org's privacy notice (when configured) is shown
 *    inline in the banner text — no burial in a modal.
 */
export function CookieConsentBanner({
  open,
  privacyPolicyUrl,
  onAcceptAll,
  onRejectAll,
  onSetCategory,
  currentCategories,
  primary,
}) {
  const [detailOpen, setDetailOpen] = useState(false);
  if (!open) return null;

  const accent = primary || '#00BCD4';

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label="Cookie consent"
      data-testid="cookie-consent-banner"
      style={{
        position: 'fixed',
        left: '1rem', right: '1rem', bottom: '1rem',
        maxWidth: 720, margin: '0 auto',
        zIndex: 40,
        background: 'rgba(15,23,42,0.96)',
        color: '#f1f5f9',
        borderRadius: 12,
        boxShadow: '0 24px 60px rgba(0,0,0,0.45)',
        padding: '1rem 1.25rem',
        fontFamily: 'system-ui, -apple-system, sans-serif',
        fontSize: '0.9rem',
        lineHeight: 1.55,
        animation: 'consentSlideUp 320ms ease',
      }}
    >
      <style>{`
        @keyframes consentSlideUp {
          from { transform: translateY(24px); opacity: 0; }
          to   { transform: translateY(0);    opacity: 1; }
        }
      `}</style>

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.85rem' }}>
        <span aria-hidden="true" style={{ fontSize: '1.5rem', lineHeight: 1 }}>🍪</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <strong style={{ fontSize: '1rem', display: 'block', marginBottom: 4 }}>
            We value your privacy
          </strong>
          <p style={{ margin: 0, color: '#cbd5e1' }}>
            We use essential cookies to make this page work. With your consent, we&apos;d also like
            to use analytics cookies to understand how visitors use our site so we can improve it.
            {privacyPolicyUrl && (
              <>
                {' '}
                <a
                  href={privacyPolicyUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-testid="cookie-consent-privacy-link"
                  style={{ color: accent, textDecoration: 'underline' }}
                >
                  Read our privacy notice
                </a>.
              </>
            )}
          </p>

          {detailOpen && (
            <div
              data-testid="cookie-consent-detail"
              style={{
                marginTop: '0.85rem',
                padding: '0.75rem',
                border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 8,
                background: 'rgba(255,255,255,0.03)',
              }}
            >
              <CategoryRow
                title="Essential cookies"
                subtitle="Required for the page to work. Cannot be turned off."
                checked
                disabled
                testId="cookie-consent-essential"
              />
              <CategoryRow
                title="Analytics cookies"
                subtitle="Google Analytics, Facebook Pixel, and any custom analytics tags configured by the nursery."
                checked={!!currentCategories?.analytics}
                onChange={(v) => onSetCategory && onSetCategory('analytics', v)}
                testId="cookie-consent-analytics"
              />
              <p style={{
                marginTop: '0.5rem', marginBottom: 0,
                fontSize: '0.78rem', color: '#94a3b8',
              }}>
                Cookie-less analytics like Plausible are also gated here to keep the choice consistent.
              </p>
            </div>
          )}

          <div style={{
            marginTop: '0.9rem', display: 'flex', gap: '0.5rem',
            flexWrap: 'wrap', alignItems: 'center',
          }}>
            <ConsentButton
              testId="cookie-consent-reject-all"
              onClick={onRejectAll}
              variant="secondary"
              accent={accent}
            >
              Reject all
            </ConsentButton>
            <ConsentButton
              testId="cookie-consent-accept-all"
              onClick={onAcceptAll}
              variant="primary"
              accent={accent}
            >
              Accept all
            </ConsentButton>
            <button
              type="button"
              onClick={() => setDetailOpen((v) => !v)}
              data-testid="cookie-consent-toggle-detail"
              aria-expanded={detailOpen}
              style={{
                background: 'transparent',
                border: 'none',
                color: '#cbd5e1',
                fontSize: '0.82rem',
                cursor: 'pointer',
                padding: '0.4rem 0.2rem',
                textDecoration: 'underline',
              }}
            >
              {detailOpen ? 'Hide preferences' : 'Preferences'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function CategoryRow({ title, subtitle, checked, disabled, onChange, testId }) {
  return (
    <label
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.6rem',
        padding: '0.35rem 0',
        cursor: disabled ? 'default' : 'pointer',
        opacity: disabled ? 0.85 : 1,
      }}
      data-testid={testId}
    >
      <input
        type="checkbox"
        checked={!!checked}
        disabled={!!disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        data-testid={`${testId}-input`}
        style={{ marginTop: 4 }}
      />
      <span style={{ flex: 1 }}>
        <span style={{ fontWeight: 600 }}>{title}</span>
        <span style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8' }}>{subtitle}</span>
      </span>
    </label>
  );
}

function ConsentButton({ children, onClick, variant, testId, accent }) {
  const isPrimary = variant === 'primary';
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      style={{
        padding: '0.55rem 1.1rem',
        border: isPrimary ? 'none' : '1px solid rgba(255,255,255,0.35)',
        borderRadius: 8,
        background: isPrimary ? accent : 'transparent',
        color: isPrimary ? '#0f172a' : '#f1f5f9',
        fontWeight: 700,
        fontSize: '0.85rem',
        cursor: 'pointer',
        letterSpacing: 0.2,
        transition: 'transform 160ms ease, filter 160ms ease',
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.filter = 'brightness(1.05)'; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.filter = 'brightness(1)'; }}
    >
      {children}
    </button>
  );
}

export default CookieConsentBanner;
